import React, { useState, useEffect, useCallback } from 'react';
import LoadingScreen from './components/LoadingScreen';
import LoginPage from './components/LoginPage';
import RegisterPage from './components/RegisterPage';
import DashboardSplash from './components/DashboardSplash';
import Tutorial from './components/Tutorial';
import Dashboard from './components/Dashboard';
import { setTokenCallbacks, setAuthTokens, clearAuthTokens } from './utils/api';
import './App.css';

type Page = 'loading' | 'login' | 'register' | 'splash' | 'tutorial' | 'dashboard';

// Idle timeout: 20 minutes in milliseconds
const IDLE_TIMEOUT = 20 * 60 * 1000;

const App: React.FC = () => {
  const [currentPage, setCurrentPage] = useState<Page>('loading');
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [refreshToken, setRefreshToken] = useState<string | null>(null);
  const [username, setUsername] = useState<string>('');
  const [isFirstLogin, setIsFirstLogin] = useState(false);
  
  // Idle timeout tracking
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Handle logout
  const handleLogout = useCallback(() => {
    clearAuthTokens();
    localStorage.removeItem('lastActivity');
    localStorage.removeItem('username');
    setAccessToken(null);
    setRefreshToken(null);
    setUsername('');
    setCurrentPage('login');
  }, []);

  // Handle token refresh from API utility
  const handleTokenRefresh = useCallback((newAccessToken: string, newRefreshToken: string) => {
    if (newAccessToken) {
      setAccessToken(newAccessToken);
    }
    if (newRefreshToken) {
      setRefreshToken(newRefreshToken);
    }
    // Tokens are already stored by api.ts via setAuthTokens
  }, []);

  // Set up API callbacks for token refresh
  useEffect(() => {
    setTokenCallbacks(handleTokenRefresh, handleLogout);
  }, [handleTokenRefresh, handleLogout]);

  // Track user activity
  const updateActivity = useCallback(() => {
    const now = Date.now();
    setLastActivity(now);
    localStorage.setItem('lastActivity', now.toString());
  }, []);

  // Set up idle timeout checking
  useEffect(() => {
    if (currentPage === 'dashboard' || currentPage === 'tutorial') {
      const checkIdleTimeout = () => {
        const storedActivity = localStorage.getItem('lastActivity');
        const lastActivityTime = storedActivity ? parseInt(storedActivity, 10) : Date.now();
        
        if (Date.now() - lastActivityTime > IDLE_TIMEOUT) {
          handleLogout();
          alert('You have been logged out due to inactivity.');
        }
      };

      // Check every minute
      const interval = setInterval(checkIdleTimeout, 60000);
      
      // Activity listeners
      const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];
      events.forEach(event => {
        document.addEventListener(event, updateActivity, { passive: true });
      });

      return () => {
        clearInterval(interval);
        events.forEach(event => {
          document.removeEventListener(event, updateActivity);
        });
      };
    }
  }, [currentPage, handleLogout, updateActivity]);

  // Clear session on initial page load only (refresh = logout)
  // But keep tutorial progress saved
  useEffect(() => {
    const hasSession = sessionStorage.getItem('voltSessionActive');
    if (!hasSession) {
      clearAuthTokens();
      localStorage.removeItem('lastActivity');
      localStorage.removeItem('username');
      sessionStorage.setItem('voltSessionActive', 'true');
    }
    // Note: tutorialProgress and tutorialCompleted are preserved
  }, []);

  const handleLoadingComplete = () => {
    if (accessToken) {
      // Already logged in, go to splash
      setCurrentPage('splash');
    } else {
      setCurrentPage('login');
    }
  };

  const handleSwitchToRegister = () => {
    setCurrentPage('register');
  };

  const handleSwitchToLogin = () => {
    setCurrentPage('login');
  };

  const handleLoginSuccess = (newAccessToken: string, newRefreshToken: string, newUsername: string, firstLogin: boolean) => {
    setAccessToken(newAccessToken);
    setRefreshToken(newRefreshToken);
    setUsername(newUsername);
    setIsFirstLogin(firstLogin);
    // Store tokens in both memory and localStorage via api module
    setAuthTokens(newAccessToken, newRefreshToken);
    localStorage.setItem('username', newUsername);
    updateActivity();
    setCurrentPage('splash');
  };

  const handleSplashComplete = () => {
    if (isFirstLogin) {
      setCurrentPage('tutorial');
    } else {
      setCurrentPage('dashboard');
    }
  };

  const handleTutorialComplete = () => {
    localStorage.setItem('tutorialCompleted', 'true');
    localStorage.removeItem('tutorialProgress'); // Clear saved progress
    setIsFirstLogin(false);
    setCurrentPage('dashboard');
  };

  const handleLicenseClaimed = () => {
    // Show splash screen after claiming license
    setCurrentPage('splash');
  };

  return (
    <div className="app">
      {currentPage === 'loading' && <LoadingScreen onComplete={handleLoadingComplete} />}
      {currentPage === 'login' && (
        <LoginPage 
          onSwitchToRegister={handleSwitchToRegister} 
          onLoginSuccess={handleLoginSuccess}
        />
      )}
      {currentPage === 'register' && <RegisterPage onSwitchToLogin={handleSwitchToLogin} />}
      {currentPage === 'splash' && (
        <DashboardSplash 
          username={username || 'User'} 
          onComplete={handleSplashComplete} 
        />
      )}
      {currentPage === 'tutorial' && (
        <Tutorial 
          username={username} 
          onComplete={handleTutorialComplete} 
        />
      )}
      {currentPage === 'dashboard' && (
        <Dashboard 
          username={username || 'User'} 
          onLogout={handleLogout}
          onLicenseClaimed={handleLicenseClaimed}
          onRedoTutorial={() => setCurrentPage('tutorial')}
        />
      )}
    </div>
  );
};

export default App;
